import { useEffect, useState } from "react";
import { CheckCircle2, Flag as FlagIcon, ClockAlert, FileText, RadioTower, Clock } from "lucide-react";
import type { PositionedFlag } from "../helpers/focusViewTypes";
import { cnrForFlag, formatCountdown } from "../helpers/focusViewHelpers";

interface FocusFlagsPanelProps {
  active: PositionedFlag[];
  missed: PositionedFlag[];
  resolved: PositionedFlag[];
  cnrBlocks: { lb: string; s: number; e: number }[] | undefined;
  resolvedIds: Set<string>;
  onToggleResolve: (flagId: string) => void;
}

function FlagTag({ tp, tg }: { tp: string; tg: string }) {
  const cls = tp === "pii" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800";
  return <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-[3px] ${cls}`}>{tg}</span>;
}

function CnrLine({ label }: { label: string | null }) {
  if (!label) return null;
  return (
    <div className="flex items-center gap-1 text-[10px] text-gray-400 font-medium mb-1">
      <FileText size={11} className="text-indigo-500" />
      {label}
    </div>
  );
}

function ResolveCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="cursor-pointer accent-blue-600 w-[13px] h-[13px]"
      />
      Resolve this flag
    </label>
  );
}

function ActiveFlagItem({
  flag,
  cnrLabel,
  checked,
  onToggle,
}: {
  flag: PositionedFlag;
  cnrLabel: string | null;
  checked: boolean;
  onToggle: () => void;
}) {
  const [countdown, setCountdown] = useState(() =>
    flag.deadline ? formatCountdown(flag.deadline, Date.now()) : null
  );

  useEffect(() => {
    if (!flag.deadline) return;
    const tick = () => setCountdown(formatCountdown(flag.deadline!, Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [flag.deadline]);

  return (
    <div className="border border-gray-200 rounded-[8px] p-[11px_13px] mb-2">
      <div className="flex items-center gap-1.5 mb-1">
        <FlagTag tp={flag.tp} tg={flag.tg ?? flag.ds} />
        <span className="text-[11px] text-gray-400">{flag.tm}</span>
      </div>
      <CnrLine label={cnrLabel} />
      <div className="text-xs text-gray-900 mb-[3px] leading-snug">{flag.ds}</div>
      <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-2">
        <Clock size={12} />
        {flag.dr}
      </div>
      {countdown && (
        <div
          className={`flex items-center gap-1 text-[11px] font-medium mb-2 ${
            countdown.expired ? "text-amber-700" : countdown.warning ? "text-amber-700" : "text-blue-600"
          }`}
        >
          {countdown.expired ? <ClockAlert size={13} /> : <Clock size={13} />}
          {countdown.label}
        </div>
      )}
      <ResolveCheckbox checked={checked} onChange={onToggle} />
    </div>
  );
}

function MissedFlagItem({
  flag,
  cnrLabel,
  checked,
  onToggle,
}: {
  flag: PositionedFlag;
  cnrLabel: string | null;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border border-gray-200 rounded-[8px] p-[11px_13px] mb-2 bg-gray-100/60 opacity-90">
      <div className="flex items-center gap-1.5 mb-1">
        <FlagTag tp={flag.tp} tg={flag.tg ?? flag.ds} />
        <span className="text-[11px] text-gray-400">{flag.tm}</span>
      </div>
      <CnrLine label={cnrLabel} />
      <div className="text-xs text-gray-900 mb-[3px] leading-snug">{flag.ds}</div>
      <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-1.5">
        <Clock size={12} />
        {flag.dr}
      </div>
      <div className="flex items-center gap-1 text-[10px] text-amber-700 font-medium mb-2">
        <RadioTower size={12} />
        Live stream has passed — edits apply to archive only
      </div>
      <ResolveCheckbox checked={checked} onChange={onToggle} />
    </div>
  );
}

function ResolvedFlagItem({ flag, cnrLabel }: { flag: PositionedFlag; cnrLabel: string | null }) {
  return (
    <div className="border border-blue-600/25 rounded-[8px] p-[11px_13px] mb-2 opacity-55">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-[3px] bg-gray-100 text-gray-500 opacity-60">
          {flag.tg ?? flag.ds}
        </span>
        <span className="text-[11px] text-gray-400">{flag.tm}</span>
        <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-[3px] font-semibold ml-1">
          ✓ Resolved
        </span>
      </div>
      <CnrLine label={cnrLabel} />
      <div className="text-xs text-gray-400 mb-[3px] leading-snug">{flag.ds}</div>
      <div className="flex items-center gap-1 text-[11px] text-gray-400">
        <Clock size={12} />
        {flag.dr}
      </div>
    </div>
  );
}

export function FocusFlagsPanel({
  active,
  missed,
  resolved,
  cnrBlocks,
  resolvedIds,
  onToggleResolve,
}: Readonly<FocusFlagsPanelProps>) {
  const noFlags = active.length === 0 && missed.length === 0 && resolved.length === 0;

  return (
    <div className="fv-section-body">
      <div className="text-xs font-semibold text-gray-900 mb-2.5 flex items-center gap-2">
        Flags for this room
        {active.length > 0 && (
          <span className="bg-red-100 text-red-800 text-[10px] px-1.5 py-px rounded-full font-medium">
            {active.length} active
          </span>
        )}
        {missed.length > 0 && (
          <span className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-px rounded-full font-medium">
            {missed.length} missed
          </span>
        )}
      </div>

      {noFlags && (
        <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
          <CheckCircle2 size={18} className="text-green-500" />
          No flags for this room
        </div>
      )}

      {active.length > 0 && (
        <>
          <div className="text-[11px] font-semibold text-gray-500 flex items-center gap-1.5 mb-1.5">
            <FlagIcon size={13} className="text-red-600" /> Active
          </div>
          {active.map((f) => (
            <ActiveFlagItem
              key={f.id}
              flag={f}
              cnrLabel={cnrForFlag(f, cnrBlocks)}
              checked={resolvedIds.has(f.id)}
              onToggle={() => onToggleResolve(f.id)}
            />
          ))}
        </>
      )}

      {missed.length > 0 && (
        <>
          <div className={`text-[11px] font-semibold text-gray-500 flex items-center gap-1.5 mb-1.5 ${active.length > 0 ? "mt-3.5" : ""}`}>
            <ClockAlert size={13} className="text-amber-700" /> Missed
          </div>
          {missed.map((f) => (
            <MissedFlagItem
              key={f.id}
              flag={f}
              cnrLabel={cnrForFlag(f, cnrBlocks)}
              checked={resolvedIds.has(f.id)}
              onToggle={() => onToggleResolve(f.id)}
            />
          ))}
        </>
      )}

      {resolved.length > 0 && (
        <>
          <div
            className={`text-[11px] font-semibold text-gray-500 flex items-center gap-1.5 mb-1.5 ${
              active.length + missed.length > 0 ? "mt-3.5" : ""
            }`}
          >
            <CheckCircle2 size={13} className="text-blue-600" /> Resolved
          </div>
          {resolved.map((f) => (
            <ResolvedFlagItem key={f.id} flag={f} cnrLabel={cnrForFlag(f, cnrBlocks)} />
          ))}
        </>
      )}
    </div>
  );
}
